import { useState, useEffect, createContext, useContext } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const password = localStorage.getItem('adminPassword');
    if (password) {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  const login = async (password) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Authorization': 'Basic ' + btoa(':' + password),
        },
      });
      if (response.ok) {
        localStorage.setItem('adminPassword', password);
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('adminPassword');
    setIsAuthenticated(false);
  };

  const getAuthHeader = () => {
    const password = localStorage.getItem('adminPassword');
    return {
      'Authorization': 'Basic ' + btoa(':' + password),
    };
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, login, logout, getAuthHeader }}>
      {children}
    </AuthContext.Provider>
  );
};

// API Helper
export const adminApi = {
  getAuthHeader: () => {
    const password = localStorage.getItem('adminPassword');
    return { 'Authorization': 'Basic ' + btoa(':' + password) };
  },
  
  getFileUrl: (path) => {
    const password = localStorage.getItem('adminPassword');
    return `${API_URL}/api/admin/files/${path}?auth=${password}`;
  },

  // Dashboard
  getDashboard: async () => {
    const res = await fetch(`${API_URL}/api/admin/dashboard`, {
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },

  // Laborers
  getLaborers: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_URL}/api/admin/laborers?${params}`, {
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },

  getLaborer: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/laborers/${id}`, {
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },

  createLaborer: async (data) => {
    const res = await fetch(`${API_URL}/api/admin/laborers`, {
      method: 'POST',
      headers: { ...adminApi.getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  },

  updateLaborer: async (id, data) => {
    const res = await fetch(`${API_URL}/api/admin/laborers/${id}`, {
      method: 'PUT',
      headers: { ...adminApi.getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  },

  deleteLaborer: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/laborers/${id}`, {
      method: 'DELETE',
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },

  // Suppliers
  getSuppliers: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_URL}/api/admin/suppliers?${params}`, {
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },

  getSupplier: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/suppliers/${id}`, {
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },

  createSupplier: async (data) => {
    const res = await fetch(`${API_URL}/api/admin/suppliers`, {
      method: 'POST',
      headers: { ...adminApi.getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  },

  updateSupplier: async (id, data) => {
    const res = await fetch(`${API_URL}/api/admin/suppliers/${id}`, {
      method: 'PUT',
      headers: { ...adminApi.getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  },

  deleteSupplier: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/suppliers/${id}`, {
      method: 'DELETE',
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },

  // File Upload
  uploadFile: async (file, entityType, entityId, docType) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(
      `${API_URL}/api/admin/upload?entity_type=${entityType}&entity_id=${entityId}&doc_type=${docType}`,
      {
        method: 'POST',
        headers: adminApi.getAuthHeader(),
        body: formData,
      }
    );
    return res.json();
  },

  deleteFile: async (entityType, entityId, fileId) => {
    const res = await fetch(`${API_URL}/api/admin/files/${entityType}/${entityId}/${fileId}`, {
      method: 'DELETE',
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },

  // OCR / Business Card Scanning
  scanBusinessCard: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_URL}/api/admin/scan-card`, {
      method: 'POST',
      headers: adminApi.getAuthHeader(),
      body: formData,
    });
    return res.json();
  },

  getScannedContacts: async () => {
    const res = await fetch(`${API_URL}/api/admin/scanned-contacts`, {
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },

  deleteScannedContact: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/scanned-contacts/${id}`, {
      method: 'DELETE',
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },

  // Export
  exportLaborers: async () => {
    const res = await fetch(`${API_URL}/api/admin/export/laborers`, {
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },

  exportSuppliers: async () => {
    const res = await fetch(`${API_URL}/api/admin/export/suppliers`, {
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },

  // Partners
  getPartners: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_URL}/api/admin/partners?${params}`, {
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },

  getPartner: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/partners/${id}`, {
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },

  createPartner: async (data) => {
    const res = await fetch(`${API_URL}/api/admin/partners`, {
      method: 'POST',
      headers: { ...adminApi.getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  },

  updatePartner: async (id, data) => {
    const res = await fetch(`${API_URL}/api/admin/partners/${id}`, {
      method: 'PUT',
      headers: { ...adminApi.getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  },

  deletePartner: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/partners/${id}`, {
      method: 'DELETE',
      headers: adminApi.getAuthHeader(),
    });
    return res.json();
  },
};
